/**
 * Central API client configuration.
 *
 * Provides:
 *  - A single `API_BASE_URL` constant (origin + `/api`) and `API_ORIGIN`
 *    constant so callers don't issue requests to literal
 *    `undefined/api/...` URLs.
 *  - `withCredentials: true` set as an axios default so callers don't have
 *    to remember it per request.
 *  - The CSRF token request/response interceptors (formerly inlined inside
 *    `AuthContext`) installed once at module load — guaranteeing every axios
 *    consumer benefits from CSRF handling, not just files that happen to use
 *    the default instance.
 *  - `apiClient` re-export of the configured axios for callers that prefer
 *    a named import over the global default.
 *
 * Importing this module has the side-effect of configuring the default
 * axios instance, but those side effects are guarded so importing the
 * module more than once (e.g. via Jest module registry resets) is safe.
 * `src/index.js` imports it so configuration runs before any UI code
 * makes a request.
 *
 * Production hard-fail: if `REACT_APP_API_URL` is missing in a production
 * build, this module *throws* at import time. Crashing the bundle at boot
 * is far less harmful than silently serving an app that issues requests
 * to `undefined/api/...`. Non-production builds (development, test) fall
 * back to `http://localhost:3001` with a console warning.
 *
 * See GitHub issue #197.
 */

import axios from 'axios';

const RAW_API_URL = process.env.REACT_APP_API_URL;

// Fail fast in production builds if the env var is missing — surfacing a
// configuration mistake at boot is far better than serving an app that
// silently issues requests to `undefined/api/...`.
if (process.env.NODE_ENV === 'production' && !RAW_API_URL) {
  throw new Error(
    '[api] REACT_APP_API_URL is not set in this production build. ' +
      'Refusing to start with a localhost fallback — set REACT_APP_API_URL at build time.'
  );
}

if (process.env.NODE_ENV !== 'production' && !RAW_API_URL) {
  // eslint-disable-next-line no-console
  console.warn(
    '[api] REACT_APP_API_URL is not set; falling back to http://localhost:3001. ' +
      'Set REACT_APP_API_URL in your .env to silence this warning.'
  );
}

/**
 * Raw API origin (no path). Use this when constructing non-`/api` URLs
 * (e.g. WebSocket endpoints) or when the surrounding code prefers to
 * spell out the `/api` segment explicitly. Prefer {@link API_BASE_URL}
 * for normal REST calls.
 */
export const API_ORIGIN = RAW_API_URL || 'http://localhost:3001';

/**
 * Fully-qualified API base URL (origin + `/api`). Import this instead of
 * re-declaring `process.env.REACT_APP_API_URL + '/api'` in every file.
 */
export const API_BASE_URL = `${API_ORIGIN}/api`;

// ---------------------------------------------------------------------------
// Registered-once side effects
//
// Mutating axios.defaults and installing global interceptors at module load
// is convenient but fragile — if this module ever gets evaluated twice in
// the same JS runtime (e.g. due to a duplicated bundle, a Jest module-cache
// reset, or accidental dual imports), we'd install the interceptors twice.
// Stash a flag on the axios instance so the second evaluation no-ops loudly.
// ---------------------------------------------------------------------------

const REGISTERED_FLAG = '__portfolioTrackerApiRegistered';

let requestInterceptorId = null;
let responseInterceptorId = null;

// ---------------------------------------------------------------------------
// CSRF handling
// ---------------------------------------------------------------------------

let currentCsrfToken = null;

/**
 * Record the latest CSRF token so the request interceptor can attach it to
 * subsequent state-changing requests. Called by `AuthContext` after
 * fetching `/api/csrf-token`.
 */
export function setCsrfToken(token) {
  currentCsrfToken = token || null;
}

/** Clear the cached CSRF token (e.g. on logout). */
export function clearCsrfToken() {
  currentCsrfToken = null;
}

/** Read the current cached CSRF token (mainly for tests). */
export function getCsrfToken() {
  return currentCsrfToken;
}

const STATE_CHANGING_METHODS = ['post', 'put', 'delete', 'patch'];

/**
 * Hook used by `AuthContext` to refresh the CSRF token when the response
 * interceptor detects an `EBADCSRFTOKEN` 403. Set at runtime so this module
 * stays decoupled from React state.
 */
let csrfRefresher = null;

/**
 * Register a CSRF refresh function. Returns an unregister callback.
 * The refresher must return the new token (or throw).
 */
export function registerCsrfRefresher(refresher) {
  csrfRefresher = refresher;
  return () => {
    if (csrfRefresher === refresher) {
      csrfRefresher = null;
    }
  };
}

function installInterceptors() {
  requestInterceptorId = axios.interceptors.request.use(
    (config) => {
      const method = config.method?.toLowerCase();
      if (currentCsrfToken && STATE_CHANGING_METHODS.includes(method)) {
        config.headers = config.headers || {};
        config.headers['X-CSRF-Token'] = currentCsrfToken;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  responseInterceptorId = axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      const data = error.response?.data;
      const isCsrfError =
        error.response?.status === 403 &&
        (data?.error === 'EBADCSRFTOKEN' ||
          data?.message?.toLowerCase().includes('csrf') ||
          data?.developerMessage?.toLowerCase().includes('csrf'));

      if (isCsrfError && originalRequest && !originalRequest._retry && csrfRefresher) {
        originalRequest._retry = true;
        try {
          const newToken = await csrfRefresher();
          // Give the cookie a beat to land before retrying.
          await new Promise((resolve) => setTimeout(resolve, 50));
          if (newToken) {
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers['X-CSRF-Token'] = newToken;
          }
          return axios(originalRequest);
        } catch (refreshError) {
          // eslint-disable-next-line no-console
          console.error('Failed to refresh CSRF token:', refreshError);
          return Promise.reject(error);
        }
      }

      return Promise.reject(error);
    }
  );
}

function registerOnce() {
  if (axios[REGISTERED_FLAG]) {
    // Loud no-op: surface duplicate installs in dev so we catch bundling
    // mistakes early, but don't crash the app in production.
    // eslint-disable-next-line no-console
    console.warn(
      '[api] lib/api.js was evaluated more than once — skipping duplicate ' +
        'axios configuration. This usually indicates a duplicated bundle or ' +
        'an unintended Jest module-registry reset.'
    );
    return;
  }

  // Send session cookies with every request by default.
  axios.defaults.withCredentials = true;

  installInterceptors();

  // Mark the instance so a second evaluation can detect itself.
  Object.defineProperty(axios, REGISTERED_FLAG, {
    value: true,
    writable: false,
    configurable: true, // allow tests to reset
    enumerable: false,
  });
}

registerOnce();

/**
 * Test-only helper. Tears down the registered-once guard and the installed
 * interceptors so a fresh module evaluation (or explicit re-registration)
 * can install them again. Not exported via the default index; tests import
 * by name.
 *
 * Safe no-op outside of `NODE_ENV === 'test'`.
 */
export function __resetForTests() {
  if (process.env.NODE_ENV !== 'test') return;
  if (requestInterceptorId !== null) {
    axios.interceptors.request.eject(requestInterceptorId);
    requestInterceptorId = null;
  }
  if (responseInterceptorId !== null) {
    axios.interceptors.response.eject(responseInterceptorId);
    responseInterceptorId = null;
  }
  currentCsrfToken = null;
  csrfRefresher = null;
  if (axios[REGISTERED_FLAG]) {
    delete axios[REGISTERED_FLAG];
  }
}

/**
 * Test-only helper to (re)install the interceptors after `__resetForTests`.
 * Lets each test start from a known, freshly-configured baseline.
 */
export function __installForTests() {
  if (process.env.NODE_ENV !== 'test') return;
  registerOnce();
}

/**
 * The configured axios instance. Exported as a named symbol for callers
 * that prefer `import { apiClient } from '../lib/api'` over the global
 * default import.
 */
export const apiClient = axios;

export default apiClient;
